/* global jsVars */

import { autocomplete } from '@algolia/autocomplete-js';
import { searchbarProducts } from './searchbar-products';
import { searchbarCategories } from './searchbar-categories';
import { searchbarPages } from './searchbar-pages';

export default class Searchbar {
  constructor(app, selector) {
    this.app = app;

    this.overlayForPanelIsOpen = false;
    this.searchInputHasContent = false;

    this.searchbarSelector = selector;
    this.searchInputSelector = '.js-searchbar-input';
    this.searchOverlaySelector = '.js-searchbar-overlay';
    this.searchContainerSelector = '.js-searchbar-container';
    this.searchInputClearSelector = '.js-searchbar-clear';
    this.searchPanelSelector = '.js-searchbar-panel';

    this.init = this.init.bind(this);
    this.onSearchInputFocus = this.onSearchInputFocus.bind(this);
    this.onSearchInputUnfocus = this.onSearchInputUnfocus.bind(this);
    this.onSearchInputContentChange = this.onSearchInputContentChange.bind(this);
    this.onSearchInputKeydown = this.onSearchInputKeydown.bind(this);
    this.onSearchInputClearClick = this.onSearchInputClearClick.bind(this);
    this.onSearchOverlayClick = this.onSearchOverlayClick.bind(this);
    this.closeSearchbar = this.closeSearchbar.bind(this);
    this.destroy = this.destroy.bind(this);
    this.events = this.events.bind(this);

    this.init();
  }

  init() {
    this.searchbarElement = document.querySelector(this.searchbarSelector);
    if (!this.searchbarElement) return;

    this.searchContainerElement = this.searchbarElement.querySelector(this.searchContainerSelector);

    autocomplete({
      container: this.searchContainerElement,
      plugins: [searchbarProducts, searchbarCategories, searchbarPages],
      openOnFocus: false,
      placeholder: jsVars.translations.searchPlaceholder,
      debug: false,
      detachedMediaQuery: false,
      panelPlacement: 'full-width',
      classNames: {
        // https://www.algolia.com/doc/ui-libraries/autocomplete/api-reference/autocomplete-js/autocomplete/#param-classnames
        root: 'search-container',
        form: 'search-form',
        inputWrapper: 'search-input-wrapper',
        inputWrapperPrefix: 'search-input-wrapper-prefix',
        inputWrapperSuffix: 'search-input-wrapper-suffix',
        input: 'search-input js-searchbar-input',
        submitButton: 'search-submit',
        clearButton: 'search-clear js-searchbar-clear',
        panel: 'c-panel js-searchbar-panel',
        // panelLayout: 'c-panel-layout', // No longer being used, since we're using the render() method below. Keep this commented as a reference.
      },
      render({ elements, render, html }, root) {
        const productsHtml = elements['wc-products'];
        const categoriesHtml = elements['wc-product-categories'];
        const pagesHtml = elements.pages;
        const { translations } = jsVars;

        render(
          html`<div class="panel-container">
            <div class="panel-inner">
              <div class="panel-header">${translations.searchTip}</div>
              <div class="panel-content">
                <div class="panel-content--left">${categoriesHtml}${pagesHtml}</div>
                <div class="panel-content--right">${productsHtml}</div>
              </div>
            </div>
          </div>`,
          root
        );
      },
    });

    this.searchOverlayElement = this.searchbarElement.querySelector(this.searchOverlaySelector);
    this.searchInputElement = this.searchbarElement.querySelector(this.searchInputSelector);
    this.searchInputClearElement = this.searchbarElement.querySelector(this.searchInputClearSelector);

    this.events();
  }

  events() {
    this.searchInputElement.addEventListener('focusin', this.onSearchInputFocus);
    this.searchInputElement.addEventListener('focusout', this.onSearchInputUnfocus);
    this.searchInputElement.addEventListener('input', this.onSearchInputContentChange);
    this.searchInputElement.addEventListener('keydown', this.onSearchInputKeydown);
    this.searchInputClearElement.addEventListener('click', this.onSearchInputClearClick);
    this.searchOverlayElement.addEventListener('click', this.onSearchOverlayClick);
  }

  onSearchInputKeydown(event) {
    if (this.overlayForPanelIsOpen) {
      if (event.key === 'Escape') {
        this.closeSearchbar();
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        const panel = document.querySelector(this.searchPanelSelector);
        if (!panel) return;
        const firstProduct = panel.querySelector('.js-searchbar-panel-product');
        const firstCategory = panel.querySelector('.js-searchbar-panel-category');
        const firstPage = panel.querySelector('.js-searchbar-panel-page');
        if (firstProduct) {
          firstProduct.click();
          this.closeSearchbar();
        } else if (firstCategory) {
          firstCategory.click();
          this.closeSearchbar();
        } else if (firstPage) {
          firstPage.click();
          this.closeSearchbar();
        }
      }
    }
  }

  onSearchInputContentChange() {
    this.searchInputHasContent = this.searchInputElement.value !== '';
    if (this.searchInputHasContent) {
      this.showOverlayForPanel();
    } else {
      this.hideOverlayForPanel();
    }
  }

  onSearchInputClearClick() {
    this.closeSearchbar();
  }

  onSearchOverlayClick() {
    this.closeSearchbar();
  }

  onSearchInputFocus(event) {
    // Prevent the overlay from being shown again when clicking on the clear button (which is a sibling of the input)
    if (this.searchInputClearElement === event.relatedTarget) return;

    // If the input has content, show the overlay
    if (this.searchInputHasContent) this.showOverlayForPanel();
  }

  onSearchInputUnfocus(event) {
    this.closeSearchbar();
  }

  showOverlayForPanel() {
    this.overlayForPanelIsOpen = true;
    this.searchOverlayElement.classList.add('--active');
  }

  hideOverlayForPanel() {
    this.overlayForPanelIsOpen = false;
    this.searchOverlayElement.classList.remove('--active');
  }

  closeSearchbar() {
    // Clear the input value
    this.searchInputElement.value = '';

    // Trigger a change event on the input to update the autocomplete panel
    const event = new Event('input');
    this.searchInputElement.dispatchEvent(event);
  }

  destroy() {
    this.searchInputElement.removeEventListener('focusin', this.onSearchInputFocus);
    this.searchInputElement.removeEventListener('focusout', this.onSearchInputUnfocus);
    this.searchInputElement.removeEventListener('input', this.onSearchInputContentChange);
    this.searchInputElement.removeEventListener('keydown', this.onSearchInputKeydown);
    this.searchInputClearElement.removeEventListener('click', this.onSearchInputClearClick);
    this.searchOverlayElement.removeEventListener('click', this.onSearchOverlayClick);
  }
}



// WEBPACK FOOTER //
// ./scripts/components/searchbar.js